if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();
        this.loadStarted = false;
        this.visibilityObserver = null;
      }

      connectedCallback() {
        if (this.loadStarted || this.visibilityObserver) return;

        // An empty history needs no request and no invisible observer.
        if (!this.getQueryUrl()) {
          this.removeSection();
          return;
        }

        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.init();
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.init();
          },
          { rootMargin: '600px 0px', threshold: 0 }
        );

        // The inner .section is initially display:none. Observe its outer
        // Shopify wrapper so the loading point still has a page position.
        this.visibilityObserver.observe(this.closest('.shopify-section') || this);
      }

      disconnectedCallback() {
        this.stopObserving();
      }

      stopObserving() {
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = null;
      }

      init() {
        if (this.loadStarted || !this.isConnected) return;

        const queryUrl = this.getQueryUrl();
        if (!queryUrl) {
          this.removeSection();
          return;
        }

        // Stop observing before requesting: fast scrolling must not start
        // a second request while the first one is still in progress.
        this.loadStarted = true;
        this.stopObserving();

        fetch(queryUrl)
          .then((response) => {
            if (!response.ok) throw new Error(`Recently viewed products: HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(responseText, 'text/html')
              .querySelector('.shopify-section');
            const recommendations = sectionInnerHTML?.querySelector('recently-viewed-products');
            const section = recommendations?.querySelector('.section');

            if (!section || !recommendations.querySelector('.product-card')) {
              this.removeSection();
              this.dispatchEvent(new CustomEvent('recommendations:empty'));
              return;
            }

            section.classList.remove('hidden');
            this.innerHTML = recommendations.innerHTML;
            this.dispatchEvent(new CustomEvent('recommendations:loaded'));
          })
          .catch((error) => {
            console.error(error);
          });
      }

      getQueryUrl() {
        let storedItems;
        try {
          storedItems = JSON.parse(window.localStorage.getItem('hypertheme:recently-viewed') || '[]');
        } catch (_) {
          return false;
        }

        if (!Array.isArray(storedItems)) return false;

        const productId = Number(this.dataset.productId);
        const productsToShow = parseInt(this.dataset.productsToShow, 10);
        if (!this.dataset.url || !(productsToShow > 0)) return false;

        const items = [...new Set(storedItems.map(Number))]
          .filter((item) => Number.isSafeInteger(item) && item > 0 && item !== productId)
          .slice(0, productsToShow);

        if (!items.length) return false;

        return this.dataset.url + items.map((item) => 'id:' + item).join(' OR ');
      }

      removeSection() {
        this.stopObserving();
        this.remove();
      }

      sendTrekkieEvent(numberProducts) {
        if (!window.ShopifyAnalytics || !window.ShopifyAnalytics.lib || !window.ShopifyAnalytics.lib.track) {
          return;
        }
        let didPageJumpOccur = this.getBoundingClientRect().top <= window.innerHeight;

        window.ShopifyAnalytics.lib.track('Recently Viewed Products Displayed', {
          theme: Shopify.theme.name,
          didPageJumpOccur,
          numberOfRecommendationsDisplayed: numberProducts,
        });
      }
    }
  );
}
